import { Component, NgZone, OnInit } from '@angular/core';
import {
  ApexAnnotations,
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';
import {
  ApiService,
  TeamSkillData,
  TeamSkillEntry,
  UserKnowledge,
} from '../../services/api.service';
import { NgClass } from '@angular/common';
import { MultiSelect } from 'primeng/multiselect';
import { SkillmapPanelComponent } from '../skillmap-panel/skillmap-panel.component';
import { TableService } from '../../services/table.service';
import { FormsModule } from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { ProgressSpinner } from 'primeng/progressspinner';

@Component({
  selector: 'app-skillmap-learning',
  standalone: true,
  imports: [
    NgApexchartsModule,
    NgClass,
    MultiSelect,
    SkillmapPanelComponent,
    FormsModule,
    PrimeTemplate,
    ButtonDirective,
    ProgressSpinner,
  ],
  templateUrl: './skillmap.component.html',
  styleUrls: ['./skillmap.component.scss'],
})
export class SkillmapLearningComponent implements OnInit {
  chartOptions: {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    dataLabels: ApexDataLabels;
    title: ApexTitleSubtitle;
    annotations: ApexAnnotations;
    tooltip: ApexTooltip;
  } = {} as any;

  userKnowledgeData!: UserKnowledge[];
  showPanel = false;
  selectedDataLabel = '';
  teamSkillEntry?: TeamSkillEntry;
  categories: string[] = [];
  selectedCategories: string[] = [];
  private skillToTeamsMap?: TeamSkillEntry[] | undefined;

  labelSize: number = 14; // valore iniziale

  constructor(
    private apiService: ApiService,
    private tableService: TableService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.loadCategories();
    this.loadChartData();
  }

  showPanelIsFalse() {
    this.showPanel = false;
  }

  private loadCategories(): void {
    this.tableService.getSkills().subscribe(skills => {
      const uniqueCategories = new Set(skills.map(s => s.g_name));
      this.categories = Array.from(uniqueCategories);
    });
  }

  protected loadChartData(): void {
    this.apiService.getLearning().subscribe(allSkillsData => {
      this.tableService.getSkills().subscribe(skillMeta => {
        const filteredSkillNames = skillMeta
          .filter(
            skill =>
              this.selectedCategories.length === 0 ||
              this.selectedCategories.includes(skill.g_name)
          )
          .map(skill => skill.skill_name);

        const filteredData: Record<
          string,
          { teacher: number; student: number }
        > = {};
        for (const key of filteredSkillNames) {
          if (allSkillsData[key]) {
            filteredData[key] = allSkillsData[key];
          }
        }

        this.apiService
          .getSkillTeamMap()
          .subscribe((teamSkillData: TeamSkillData) => {
            this.skillToTeamsMap = teamSkillData.data;
            const totalActiveTeams = teamSkillData.activeTeamCount;

            const series = this.buildChartSeries(
              filteredData,
              this.mapSkillsToTeams(teamSkillData.data),
              totalActiveTeams
            );
            this.setupChart(series);
          });
      });
    });
  }

  private mapSkillsToTeams(
    data: TeamSkillData['data']
  ): Record<string, Set<string>> {
    const map: Record<string, Set<string>> = {};

    for (const { skill, teams } of data) {
      map[skill] = new Set(teams);
    }

    return map;
  }

  private buildChartSeries(
    allSkillsData: Record<string, { teacher: number; student: number }>,
    skillToTeamsMap: Record<string, Set<string>>,
    totalActiveTeams: number
  ): any[] {
    return Object.keys(allSkillsData).map(skillName => {
      const { teacher = 0, student = 1 } = allSkillsData[skillName] ?? {};
      const teamCount = skillToTeamsMap[skillName]?.size ?? 0;

      return {
        x: teacher,
        y: student, // Add a small offset to avoid overlap?
        meta: { label: skillName },
      };
    });
  }

  private setupChart(series: any[]): void {
    this.chartOptions = {
      series: [
        {
          name: 'Skill Level',
          data: series,
        },
      ],
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        width: '100%',
        height: '100%',
        type: 'scatter',
        events: {
          dataPointSelection: (event, chartContext, config) => {
            const selected =
              config.w.config.series[config.seriesIndex].data[
                config.dataPointIndex
              ];
            this.handlePointSelection(selected.meta.label);
          },
        },
      },
      dataLabels: {
        style: {
          colors: ['#f89fc2ff'],
          fontSize: '10px',
        },
        background: {
          padding: 12,
          foreColor: '#000',
          borderRadius: 4,
        },
        enabled: true,
        formatter: (_val, opt) =>
          opt.w.globals.initialSeries[0].data[opt.dataPointIndex].meta.label,
      },
      xaxis: {
        min: -0.5,
        max: 10,
        tickAmount: 5,
        labels: {
          formatter: val => `${Math.round(Number(val))}`,
        },
      },
      yaxis: {
        min: -0.5,
        max: 15,
        tickAmount: 5,
        labels: {
          formatter: val => `${Math.round(val)}`,
        },
      },
      title: {
        text: 'Learning Chart',
      },
      annotations: {
        xaxis: [
          {
            x: 0,
            borderColor: '#000',
            borderWidth: 2,
            strokeDashArray: 2,
            label: { text: 'Students' },
          },
        ],
        yaxis: [
          {
            y: 0,
            borderColor: '#000',
            borderWidth: 2,
            strokeDashArray: 2,
            label: { text: 'Teachers' },
          },
        ],
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const point = w.config.series[seriesIndex].data[dataPointIndex];
          return `
          <div class="tooltip">
            <p><strong>${point.meta.label}</strong></p>
            <p>Teacher <strong>${Math.round(point.x)}</strong></p>
            <p>Students <strong>${Math.round(point.y)}</strong></p>
          </div>`;
        },
      },
    };
  }

  private handlePointSelection(skillName: string): void {
    this.ngZone.run(() => {
      this.showPanel = true;

      this.teamSkillEntry = this.skillToTeamsMap?.find(
        st => st.skill == skillName
      );

      this.selectedDataLabel = skillName;
      this.apiService.getSkillUserLearning(skillName).subscribe(data => {
        this.userKnowledgeData = data;
      });
    });
  }

  increaseLabelSize() {
    if (this.labelSize < 20) {
      this.labelSize++;
      // Aggiorna la fontSize nei chartOptions
      this.chartOptions = {
        ...this.chartOptions,
        dataLabels: {
          ...this.chartOptions.dataLabels,
          style: {
            ...this.chartOptions.dataLabels.style,
            fontSize: `${this.labelSize}px`,
          },
        },
      };
    }
  }

  decreaseLabelSize() {
    if (this.labelSize > 10) {
      this.labelSize--;
      // Aggiorna la fontSize nei chartOptions
      this.chartOptions = {
        ...this.chartOptions,
        dataLabels: {
          ...this.chartOptions.dataLabels,
          style: {
            ...this.chartOptions.dataLabels.style,
            fontSize: `${this.labelSize}px`,
          },
        },
      };
    }
  }
}
